import {
  kgToLbs,
  lbsToKg,
  pairWeightFromKg,
  pairWeightFromLbs,
  roundWeight,
} from './weightConversion';
import { getApiUrl } from './apiConfig';

export function clampInt(value, min = 1, max = 999) {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

export function clampSeconds(value, min = 0, max = 9999) {
  const n = parseInt(String(value), 10);
  if (Number.isNaN(n)) return min;
  return Math.min(max, Math.max(min, n));
}

export function isTimeBasedExercise(exercise) {
  return (
    exercise?.is_time_based_exercise === true ||
    exercise?.is_time_based_exercise === 'true' ||
    exercise?.isTimeBasedExercise === true
  );
}

export function isHeightBasedExercise(exercise) {
  return (
    exercise?.is_height_based === true ||
    exercise?.is_height_based === 'true' ||
    exercise?.isHeightBased === true
  );
}

export function isWeightBasedExercise(exercise) {
  return (
    exercise?.is_weight_based === true ||
    exercise?.is_weight_based === 'true' ||
    exercise?.isWeightBased === true
  );
}

export function defaultRepHeight(exercise) {
  const h = exercise?.height;
  return h != null && String(h).trim() !== '' ? String(h).trim() : null;
}

export function defaultRepSeconds(exercise) {
  if (exercise?.time != null && exercise.time !== '') {
    return clampSeconds(exercise.time, 0);
  }
  return clampSeconds(exercise?.reps ?? 30, 0);
}

export function buildPerformanceSet(setNumber, prescribedReps, exercise = null, weightPair = null) {
  const base = {
    client_id: `set-${setNumber}-${Date.now()}-${Math.random()}`,
    set_number: setNumber,
    prescribed_reps: clampInt(prescribedReps),
    height: defaultRepHeight(exercise),
    seconds: isTimeBasedExercise(exercise) ? defaultRepSeconds(exercise) : null,
  };
  if (weightPair?.weight_kg != null || weightPair?.weight_lbs != null) {
    base.weight_kg = weightPair.weight_kg ?? null;
    base.weight_lbs = weightPair.weight_lbs ?? null;
  }
  return base;
}

export function createDefaultPerformanceSets(
  prescribedSets,
  prescribedReps,
  exercise = null,
  weightPair = null
) {
  const setCount = clampInt(prescribedSets);
  const repsEach = clampInt(prescribedReps);
  return Array.from({ length: setCount }, (_, i) =>
    buildPerformanceSet(i + 1, repsEach, exercise, weightPair)
  );
}

export function applyWeightDefaultsToExercise(exercise, defaultKg, defaultLbs) {
  const hasKg = defaultKg != null && defaultKg !== '';
  const hasLbs = defaultLbs != null && defaultLbs !== '';
  if (!hasKg && !hasLbs) return exercise;
  const weightPair = hasKg
    ? pairWeightFromKg(defaultKg)
    : pairWeightFromLbs(defaultLbs);
  const performance_sets = (exercise.performance_sets || []).map((s) => ({
    ...s,
    weight_kg: weightPair.weight_kg,
    weight_lbs: weightPair.weight_lbs,
  }));
  return syncExercisePrescribedCounts({ ...exercise, performance_sets });
}

export function attachPerformanceToExercise(exercise) {
  const base = {
    ...exercise,
    is_time_based_exercise: isTimeBasedExercise(exercise),
    is_height_based: isHeightBasedExercise(exercise),
    is_weight_based: isWeightBasedExercise(exercise),
    time: exercise?.time ?? null,
    sets: clampInt(exercise?.sets ?? 1),
    reps: clampInt(exercise?.reps ?? 1),
  };
  if (base.performance_sets?.length) {
    return syncExercisePrescribedCounts(base);
  }
  return {
    ...base,
    performance_sets: createDefaultPerformanceSets(base.sets, base.reps, base),
  };
}

export function mapPerformanceSetsFromApi(apiSets, exercise = null) {
  if (!Array.isArray(apiSets) || apiSets.length === 0) return null;
  const timeBased = exercise && isTimeBasedExercise(exercise);
  const heightBased = exercise && isHeightBasedExercise(exercise);
  const fallbackSeconds = timeBased ? defaultRepSeconds(exercise) : null;
  const fallbackHeight = heightBased ? defaultRepHeight(exercise) : null;

  return apiSets.map((set) => {
    const prescribedReps = clampInt(
      set.prescribed_reps ??
        (Array.isArray(set.reps) && set.reps.length ? set.reps.length : 1)
    );
    return {
      client_id: set.id != null ? `set-db-${set.id}` : `set-${set.set_number}`,
      set_number: set.set_number,
      db_set_id: set.id ?? null,
      prescribed_reps: prescribedReps,
      height: set.height ?? fallbackHeight,
      seconds:
        set.seconds != null
          ? clampSeconds(set.seconds, 0)
          : fallbackSeconds,
      weight_kg: set.weight_kg != null ? roundWeight(set.weight_kg) : null,
      weight_lbs: set.weight_lbs != null ? roundWeight(set.weight_lbs) : null,
    };
  });
}

export function mapDbExerciseToSessionState(row) {
  const exercise = attachPerformanceToExercise({
    ...row,
    performance_sets:
      mapPerformanceSetsFromApi(row.performance_sets, row) ||
      undefined,
  });
  return exercise;
}

export function syncExercisePrescribedCounts(exercise) {
  const performance_sets = exercise.performance_sets || [];
  const sets = performance_sets.length || clampInt(exercise.sets, 1);
  const repCounts = performance_sets
    .map((s) => clampInt(s.prescribed_reps ?? 1, 1))
    .filter((n) => n > 0);
  const reps = repCounts.length ? repCounts[0] : clampInt(exercise.reps ?? 1);
  return {
    ...exercise,
    sets,
    reps,
    performance_sets,
  };
}

export function getSetDisplaySeconds(setRow, exercise) {
  if (setRow?.seconds != null && setRow.seconds !== '') {
    return clampSeconds(setRow.seconds, 0);
  }
  return defaultRepSeconds(exercise);
}

export function updateSetHoldSeconds(exercise, setClientId, seconds) {
  const n = clampSeconds(seconds, 0);
  return syncExercisePrescribedCounts({
    ...exercise,
    time: n,
    performance_sets: (exercise.performance_sets || []).map((s) =>
      s.client_id === setClientId ? { ...s, seconds: n } : s
    ),
  });
}

export function addPerformanceSet(exercise) {
  const sets = exercise.performance_sets || [];
  const nextNum = sets.length + 1;
  const defaultReps = clampInt(exercise.reps ?? 1);
  const last = sets[sets.length - 1];
  const weightPair =
    isWeightBasedExercise(exercise) && last
      ? { weight_kg: last.weight_kg ?? null, weight_lbs: last.weight_lbs ?? null }
      : null;
  return syncExercisePrescribedCounts({
    ...exercise,
    performance_sets: [...sets, buildPerformanceSet(nextNum, defaultReps, exercise, weightPair)],
  });
}

export function removePerformanceSet(exercise, setClientId) {
  const filtered = (exercise.performance_sets || []).filter((s) => s.client_id !== setClientId);
  const renumbered = filtered.map((s, i) => ({
    ...s,
    set_number: i + 1,
  }));
  return syncExercisePrescribedCounts({
    ...exercise,
    performance_sets:
      renumbered.length > 0
        ? renumbered
        : [buildPerformanceSet(1, exercise.reps ?? 1, exercise)],
  });
}

export function updateSetRepCount(exercise, setClientId, count) {
  const n = clampInt(count);
  return syncExercisePrescribedCounts({
    ...exercise,
    performance_sets: (exercise.performance_sets || []).map((s) =>
      s.client_id === setClientId ? { ...s, prescribed_reps: n } : s
    ),
  });
}

export function updateSetHeight(exercise, setClientId, height) {
  const h = height != null && String(height).trim() !== '' ? String(height).trim() : null;
  return {
    ...exercise,
    performance_sets: (exercise.performance_sets || []).map((s) =>
      s.client_id === setClientId ? { ...s, height: h } : s
    ),
  };
}

export function updateSetWeight(exercise, setClientId, displayValue, weightInKg) {
  const parsed = parseFloat(String(displayValue));
  if (Number.isNaN(parsed) || parsed < 0) {
    return syncExercisePrescribedCounts({
      ...exercise,
      performance_sets: (exercise.performance_sets || []).map((s) =>
        s.client_id === setClientId
          ? { ...s, weight_kg: null, weight_lbs: null }
          : s
      ),
    });
  }
  const pair = weightInKg ? pairWeightFromKg(parsed) : pairWeightFromLbs(parsed);
  return syncExercisePrescribedCounts({
    ...exercise,
    performance_sets: (exercise.performance_sets || []).map((s) =>
      s.client_id === setClientId
        ? { ...s, weight_kg: pair.weight_kg, weight_lbs: pair.weight_lbs }
        : s
    ),
  });
}

export function getSetDisplayWeight(setRow, weightInKg) {
  if (!setRow) return 0;
  if (weightInKg) {
    return setRow.weight_kg != null ? roundWeight(setRow.weight_kg) : 0;
  }
  return setRow.weight_lbs != null ? roundWeight(setRow.weight_lbs) : 0;
}

/** Copy set 1 reps/time, height, and weight into all other performance sets. */
export function copyFirstPerformanceSetToRemainingSets(exercise) {
  const sets = exercise?.performance_sets || [];
  if (sets.length < 2) return exercise;

  const source = sets[0];
  const sourceRepCount = clampInt(source.prescribed_reps ?? 1);
  const sourceHeight = source.height ?? null;
  const sourceSeconds = isTimeBasedExercise(exercise)
    ? getSetDisplaySeconds(source, exercise)
    : null;

  const performance_sets = sets.map((s, i) =>
    i === 0
      ? s
      : {
          ...s,
          prescribed_reps: sourceRepCount,
          height: sourceHeight,
          seconds: sourceSeconds,
          weight_kg: source.weight_kg ?? null,
          weight_lbs: source.weight_lbs ?? null,
        }
  );

  let next = { ...exercise, performance_sets };
  if (isTimeBasedExercise(exercise) && sourceSeconds != null) {
    next.time = sourceSeconds;
  }
  if (isHeightBasedExercise(exercise) && sourceHeight != null) {
    next.height = sourceHeight;
  }
  return syncExercisePrescribedCounts(next);
}

export function updateExerciseHeight(exercise, height) {
  const h = height != null && String(height).trim() !== '' ? String(height).trim() : null;
  return {
    ...exercise,
    height: h,
    performance_sets: (exercise.performance_sets || []).map((s) => ({
      ...s,
      height: h,
    })),
  };
}

export function performanceSetsToPayload(performance_sets, exercise = null) {
  const timeBased = exercise && isTimeBasedExercise(exercise);
  const heightBased = exercise && isHeightBasedExercise(exercise);
  const weightBased = exercise && isWeightBasedExercise(exercise);

  return (performance_sets || []).map((s) => {
    const prescribedReps = clampInt(s.prescribed_reps ?? 1);
    const base = {
      set_number: s.set_number,
      prescribed_reps: prescribedReps,
    };

    if (heightBased && s.height != null && String(s.height).trim() !== '') {
      base.height = String(s.height).trim();
    }

    if (timeBased) {
      base.seconds = clampSeconds(
        s.seconds ?? getSetDisplaySeconds(s, exercise),
        0
      );
    }

    if (weightBased) {
      if (s.weight_kg != null && s.weight_kg !== '') {
        const pair = pairWeightFromKg(s.weight_kg);
        base.weight_kg = pair.weight_kg;
        base.weight_lbs = pair.weight_lbs;
      } else if (s.weight_lbs != null && s.weight_lbs !== '') {
        const pair = pairWeightFromLbs(s.weight_lbs);
        base.weight_kg = pair.weight_kg;
        base.weight_lbs = pair.weight_lbs;
      }
    }

    return base;
  });
}

export async function fetchStudentExerciseWeightDefault(studentId, exerciseId) {
  if (!studentId || !exerciseId) return null;
  try {
    const res = await fetch(
      `${getApiUrl()}/api/student-exercise-weight-defaults?student_id=${encodeURIComponent(String(studentId))}&exercise_id=${encodeURIComponent(String(exerciseId))}`
    );
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

export async function enrichExerciseWeightFlagFromCatalog(exercise, studentId) {
  if (isWeightBasedExercise(exercise) || !exercise?.exercise_id) return exercise;
  try {
    const params = new URLSearchParams();
    if (studentId) {
      params.set('student_id', String(studentId));
      params.set('viewer_id', String(studentId));
    }
    const qs = params.toString();
    const res = await fetch(
      `${getApiUrl()}/api/exercises/${encodeURIComponent(String(exercise.exercise_id))}${qs ? `?${qs}` : ''}`
    );
    if (!res.ok) return exercise;
    const catalog = await res.json();
    if (
      catalog.is_weight_based === true ||
      catalog.is_weight_based === 'true' ||
      catalog.isWeightBased === true
    ) {
      return { ...exercise, is_weight_based: true };
    }
  } catch {
    /* ignore */
  }
  return exercise;
}

export async function attachExerciseWithWeightDefaults(exercise, studentId) {
  const enriched = await enrichExerciseWeightFlagFromCatalog(exercise, studentId);
  const withPerf = attachPerformanceToExercise(enriched);
  if (!isWeightBasedExercise(withPerf) || !studentId || !withPerf.exercise_id) {
    return withPerf;
  }
  const defaults = await fetchStudentExerciseWeightDefault(studentId, withPerf.exercise_id);
  if (defaults?.default_weight_kg != null || defaults?.default_weight_lbs != null) {
    return applyWeightDefaultsToExercise(
      withPerf,
      defaults.default_weight_kg,
      defaults.default_weight_lbs
    );
  }
  return withPerf;
}
